import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import PropTypes from 'prop-types'

import { NumberInput, SelectInput } from '../Inputs/Inputs'
import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'

const SleepInterval = ({ device }) => {
  const defaultSettings = {
    settings: {
      sleepInterval: 900,
      update: 0,
    },
  }

  const {
    application_settings: applicationSettings = defaultSettings,
    application_settings_new: applicationSettingsNew = defaultSettings,
  } = device || {}

  const {
    settings: {
      sleepInterval: defaultSleepInterval = 900,
      update: defaultUpdate = 0,
    } = {},
  } = applicationSettings
  const {
    settings: {
      sleepInterval: newSleepInterval,
      update: newUpdate,
    } = {},
  } = applicationSettingsNew

  const scaleToMinutes = useCallback((value) => {
    if (value === null || value === undefined) return null
    const numericValue = Number(value)
    if (Number.isNaN(numericValue)) return null
    return Math.round(numericValue / 60)
  }, [])

  const initialSleepInterval = useMemo(() => {
    const scaledNew = scaleToMinutes(newSleepInterval)
    if (scaledNew === 0 || scaledNew) return scaledNew
    const scaledDefault = scaleToMinutes(defaultSleepInterval)
    return (scaledDefault === 0 || scaledDefault) ? scaledDefault : 0
  }, [defaultSleepInterval, newSleepInterval, scaleToMinutes])

  const normalizeUpdate = useCallback((value) => {
    if (value === 0 || value === 1) return value
    if (value === '0' || value === '1') return Number(value)
    return defaultUpdate === 1 ? 1 : 0
  }, [defaultUpdate])

  const [sleepInterval, setSleepInterval] = useState(initialSleepInterval ?? 0)
  const [update, setUpdate] = useState(normalizeUpdate(newUpdate))

  const dispatchSettings = useCallback((nextSleepInterval, nextUpdate) => {
    const { address } = device || {}
    if (!address) return

    const sleepMinutes = Number(
      nextSleepInterval ?? sleepInterval ?? 0
    )
    const firmwareUpdate = Number(
      nextUpdate ?? update ?? 0
    )

    AppDispatcher.dispatch({
      action: 'change-setting',
      setting: {
        address,
        status: 'new',
        settings: {
          sleepInterval: sleepMinutes * 60,
          update: firmwareUpdate,
        },
      },
    })
  }, [device, sleepInterval, update])

  useEffect(() => {
    dispatchSettings(sleepInterval, update)
  }, [dispatchSettings, sleepInterval, update])

  const childChange = useCallback((event) => {
    const { target: { name, value } } = event
    let newValue = value
    if (name === 'sleepInterval') {
      const numericValue = Number(newValue)
      const clampedValue = Math.min(Math.max(numericValue, 0), 360)
      setSleepInterval(clampedValue)
      dispatchSettings(clampedValue, update)
    } else if (name === 'update') {
      const normalizedUpdate = normalizeUpdate(Number(newValue))
      setUpdate(normalizedUpdate)
      dispatchSettings(sleepInterval, normalizedUpdate)
    }
  }, [dispatchSettings, normalizeUpdate, sleepInterval, update])

  return (
    <div>
      <div className={styles.inputArea}>
        <section>
          <h5>
            <FormattedMessage
              id='setting.sleepInterval.label'
              defaultMessage='Sleep Interval'
              description='sleep interval label'
            />
          </h5>
          <div className={styles.otherNumber}>
            <NumberInput
              value={sleepInterval}
              childChange={childChange}
              field='sleepInterval'
              max={360}
              min={0}
              steps={1}
              style={styles.otherNumberInput}
            />
          </div>
          <div>
            <FormattedMessage
              id='setting.minutes'
              defaultMessage='minutes'
              description='minutes label'
            />
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.update.label'
              defaultMessage='Firmware Updates'
              description='Update label'
            />
          </h5>
          <div className={styles.otherSelect}>
            <SelectInput
              value={update}
              childChange={childChange}
              field='update'
              options={[
                { value: 0, name: 'OFF' },
                { value: 1, name: 'ON' },
              ]}
              style={styles.otherSelectInput}
            />
          </div>
        </section>
      </div>
    </div>
  )
}

SleepInterval.propTypes = {
  device: PropTypes.object.isRequired,
}

export default SleepInterval
